/**
 * Ryan O'Dowd
 * 2023-05-02
 * © Copyright 2023 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  PRACTICE_ROUTE,
  READ_ROUTE,
  setPreferredVerseSelectionMethod,
  setShowBadgesInRead,
} from '@utils/store/actions';
import React, {
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import AddToGoalDialog from '@components/AddToGoalDialog';
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
import Dialog from '@components/common/Dialog';
import Icon from '@react-native-vector-icons/material-design-icons';
import {
  IconButton,
} from 'react-native-paper';
import PassageDisplay from '@components/PassageDisplay';
import ShareButton from '@components/ShareButton';
import SpritzDialog from '@components/SpritzDialog';
import Text from '@components/common/Text';
import getStyles from './styles';
import hooks from '@hooks';
import {
  useNetInfo,
} from '@react-native-community/netinfo';
import utilities from '@utils/utilities';

const ICON_SIZE = 21;

const SelectionMenu = (props: SelectionMenuProps) => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles, {bottomBorderColorOverride: props.bottomBorderColorOverride});

  const [_isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);
  const [_showOtherTranslations, setShowOtherTranslations] = useState(false);
  const [_isSpritzDialogOpen, setIsSpritzDialogOpen] = useState(false);

  const settingsPreferredVerseSelectionMethod = hooks.useTypedSelector((state) => state.settingsPreferredVerseSelectionMethod);
  const userSettings = hooks.useTypedSelector((state) => state.userSettings);

  const betaOn = hooks.useTypedSelector((state) => state.userSettings.beta_on);

  const showBadgesInRead = hooks.useTypedSelector((state) => state.settingsShowBadgesInRead);

  const verseNumsString = props.passage?.prettyPassageName().split(':').pop();

  const updateGoalLoading = hooks.useTypedSelector((state: any) => state.loadingStates.apiUpdateGoal);

  let allVerses = hooks.useGetVersesForPassages(props.passage ? [props.passage] : []); // @TODO: this doesn't get recalculated when Bible is deleted locally (edge case, but something to consider)
  allVerses = Object.values(allVerses)[0] ?? [];

  const netInfo = useNetInfo();
  const updateDisabled = !netInfo.isConnected || updateGoalLoading;

  const selectedVersesTitle = props.referenceOnLineTwo ? null : ( // @TODO: if line 2, display better...this is a temporary hack because the ui is tricky...not sure how to display the reference best.  but users shouldn't need the selected numbers displayed right now
    <View style={styles.referenceWrapper}>
      <Text
        style={styles.contextMenuReference}
        variant='bodyLarge'// @TODO: not working
      >
        {verseNumsString ? `${verseNumsString.includes('-') ? i18n('verses') : i18n('verse')} ${verseNumsString}` : ''}
      </Text>
    </View>
  );

  const passageBook = props.passage?.getBook();

  return (
    <View style={styles.container}>{/* @TODO: this should be sticky to the top of the passage on Home/Passage view */}
      <View style={[styles.innerContainer, props.referenceOnLineTwo && styles.referenceOnLineTwo]}>
        {!props.referenceOnLineTwo && selectedVersesTitle}
        <View style={styles.menuOptions}>
          {!!(betaOn && props.showToggleBadges) && // @TODO: take out of beta? probably not until there's more logic...this might only be helpful for me for a while, but when it's GA, it shouldn't be an option when there are no started wrinkles in the current passage // @TODO: i think this should be hidden in an overflow menu
            <View style={styles.foo}>{/* @TODO: a litle too hacky for the badge to show up */}
              <IconButton
                style={styles.contextMenuButton}
                icon='star'// @TODO: better icon?
                iconColor={props.iconColorOverride || styles.colors.white}
                size={ICON_SIZE}
                onPress={() => dispatch(setShowBadgesInRead(!showBadgesInRead))}
              />
              {!!showBadgesInRead && <View style={styles.badge} />}
            </View>
          }
          {!!(betaOn && props.spritzPassage) && // @TODO: take out of beta? probably not until there's more logic...this might only be helpful for me for a while // @TODO: ask matt hannan and others about using this feature in classrooms
            <IconButton
              style={styles.contextMenuButton}
              icon='lightning-bolt'// @TODO: better icon?
              iconColor={props.iconColorOverride || styles.colors.white}
              size={ICON_SIZE}
              onPress={() => setIsSpritzDialogOpen(true)}
            />
          }
          {!props.hideShare &&
            <ShareButton
              style={styles.contextMenuButton}
              passage={props.passage}
              passageText={utilities.versesToText(allVerses)}
              iconColor={props.iconColorOverride}
              size={ICON_SIZE}
              locationForAnalytics='mobile_home_passage_of_the_day_widget'
            />
          }
          {!props.hideCompare &&
            <IconButton
              style={styles.contextMenuButton}
              disabled={!props.passage}
              icon='bookshelf'
              iconColor={props.iconColorOverride || styles.colors.white}
              size={ICON_SIZE}
              onPress={() => setShowOtherTranslations(true)}
            />
          }
          {!props.hideNumbersTextToggle &&
            <IconButton
              style={styles.contextMenuButton}
              icon={settingsPreferredVerseSelectionMethod === 'text' ? 'dialpad' : 'format-letter-case'}
              iconColor={props.iconColorOverride || styles.colors.white}
              size={ICON_SIZE}
              onPress={() => dispatch(setPreferredVerseSelectionMethod(settingsPreferredVerseSelectionMethod === 'numbers' ? 'text' : 'numbers'))}
            />
          }
          {!props.hideReadInContext && props.passageForContext &&
            <IconButton
              style={styles.contextMenuButton}
              icon='book'
              iconColor={props.iconColorOverride || styles.colors.white}
              size={ICON_SIZE}
              onPress={() => {
                const chapterId = CPassage.getChapterByBookIdAndNumber(props.passageForContext.book_id, props.passageForContext.chapter_number).id;
                navigation.navigate(READ_ROUTE, {
                  chapterId,
                  contextPassage: props.passageForContext,
                });
              }}
            />
          }
          {!props.hideAddToGoal &&
            <View>
              <IconButton
                style={styles.contextMenuButton}
                disabled={!props.passage || updateDisabled}
                icon='playlist-plus'
                iconColor={props.iconColorOverride || styles.colors.white}
                size={ICON_SIZE}
                onPress={() => setIsGoalDialogOpen(true)}
              />
              {(props.passage && updateDisabled) && <Icon style={styles.offlineIcon} name='sync-alert' />}
            </View>
          }
          {!props.hideTest &&
            <IconButton
              style={styles.contextMenuButton}
              disabled={!props.passage}
              icon='test-tube'
              iconColor={props.iconColorOverride || styles.colors.white}
              size={ICON_SIZE}
              onPress={() => {
                navigation.navigate(PRACTICE_ROUTE, {
                  passages: [props.passage],
                  noGoal: props.isUnattachedToGoal,
                  isTest: true,
                  assignmentId: props.assignmentId,
                });
              }}
            />
          }
          {!props.hideLesson &&
            <IconButton
              style={styles.contextMenuButton}
              disabled={!props.passage}
              icon='school'
              iconColor={props.iconColorOverride || styles.colors.white}
              size={ICON_SIZE}
              onPress={() => {
                navigation.navigate(PRACTICE_ROUTE, {
                  passages: [props.passage],
                  noGoal: props.isUnattachedToGoal,
                  assignmentId: props.assignmentId,
                });
              }}
            />
          }
          <IconButton
            style={styles.contextMenuButton}
            disabled={!props.selectedVerseNums.length}
            icon='close'
            iconColor={props.iconColorOverride || styles.colors.white}
            size={ICON_SIZE}
            onPress={() => props.clearSelection()}
          />
        </View>
        {props.referenceOnLineTwo && selectedVersesTitle}
      </View>
      {_showOtherTranslations &&
        <Dialog
          visible={_showOtherTranslations}
          onDismiss={() => setShowOtherTranslations(false)}
        >{/* @TODO: abstract this modal to it's own component and make available in Settings component as well */}
          <Dialog.Title>{props.passage?.prettyPassageName() ?? ''}</Dialog.Title>
          <Dialog.Content>
            <View style={styles.compareInner}>
              {[userSettings.preferred_translation_id, ...((userSettings.favorite_translation_ids?.length > 1 ? (userSettings.favorite_translation_ids || []) : [...(userSettings.favorite_translation_ids || []), 2, 5, 3, 6, 4]).filter((translationId) => translationId !== userSettings.preferred_translation_id))].map((translationId) => { // @NOTE: ESV, BSB, NIV, NKJV, and KJV are the defaults
                const translation = props.translations.find((t) => t.id === translationId);
                return (
                  <TouchableOpacity// @TODO: replace with ListItem
                    key={translation.id}
                    style={styles.translationComparisonWrapper}
                    onPress={() => {
                      const chapterId = CPassage.getChapterByBookIdAndNumber(passageBook.id, props.passage.chapter_number).id;
                      navigation.navigate(READ_ROUTE, {
                        chapterId,
                        contextPassage: props.passage.cloneWithChanges({
                          translationId: translation.id,
                          startVerseNumber: props.selectedVerseNums[0],
                          endVerseNumber: props.selectedVerseNums[props.selectedVerseNums.length - 1],
                        }),
                      });
                      setShowOtherTranslations(false);
                    }}
                  >
                    <View style={styles.translationWrapper}>
                      <Text style={[styles.translationNameAndAbbreviation, styles.translationName]}>
                        {translation.name}
                      </Text>
                      <Text style={[styles.translationNameAndAbbreviation, styles.translationAbbreviation]}>
                        {translation.abbreviation}
                      </Text>
                    </View>
                    <View style={styles.compareVersesWrapperContainer}>
                      <PassageDisplay
                        key={translation.id}
                        passages={[
                          props.passage.cloneWithChanges({
                            translationId: translation.id,
                            startVerseNumber: allVerses[0].rangeStartVerseNumber ?? props.passage.startVerseNumber,
                            endVerseNumber: allVerses[allVerses.length - 1].rangeEndVerseNumber ?? props.passage.endVerseNumber,
                          }),
                        ]}
                        isPressDisabled={true}
                        showFormatting={true}
                        showFootnotes={true}
                        showHeadings={true}// @TODO: false seems best?
                        showContextHighlight={false}
                        hideNumbersTextToggle={true}
                        hideReadInContext={true}
                        hideLicensing={true}
                        showToggleBadges={false}
                        isUnattachedToGoal={true}
                        showWrinkleBadges={false}
                        alwaysShowSelectionMenu={false}
                      />
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='contained'
              onPress={() => setShowOtherTranslations(false)}
            >
              {i18n('close')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
      {_isGoalDialogOpen && (
        <AddToGoalDialog
          onClose={() => setIsGoalDialogOpen(false)}
          passage={props.passage}
        />
      )}
      {_isSpritzDialogOpen &&
        <SpritzDialog
          passage={props.spritzPassage}// @TODO: if verses are selected, show only the selected verses?
          onClose={() => setIsSpritzDialogOpen(false)}
          goToPreviousPassage={props.spritzGoToPreviousPassage}
          goToNextPassage={props.spritzGoToNextPassage}
          assignmentId={props.assignmentId}
        />
      }
    </View>
  );
};

class SelectionMenuProps {
  translations: any[];
  selectedVerseNums: number[]; // @TODO: maybe don't need this since the passage is passed in
  clearSelection: () => void;

  passage?: any;
  passageForContext?: any;
  hideCompare?: boolean;
  hideAddToGoal?: boolean;
  hideShare?: boolean;
  hideTest?: boolean;
  hideLesson?: boolean;
  hideNumbersTextToggle?: boolean;
  hideReadInContext?: boolean;
  isUnattachedToGoal?: boolean;
  iconColorOverride?: string;
  bottomBorderColorOverride?: string;
  assignmentId?: number;
  referenceOnLineTwo?: boolean;
  showToggleBadges?: boolean;
  spritzPassage?: any;
  spritzGoToPreviousPassage?: () => void; // @TODO: verify signature
  spritzGoToNextPassage?: () => void; // @TODO: verify signature
};

export default SelectionMenu;
